import { motion, useReducedMotion } from "framer-motion";

// Reusable entrance-stagger primitives. These wrap a grid/list from the OUTSIDE
// so shared components (e.g. PlanCard) never need to change. Motion is purely
// decorative reveal — it never gates interaction.
//
// Emil/design-eng decisions applied:
//  - ease-out (entering), duration < 300ms, 60ms stagger between children.
//  - Nothing appears from nothing: children fade + slide 10px, no scale-from-0.
//  - prefers-reduced-motion: keep the opacity fade (aids comprehension), drop
//    the y-translation entirely (no movement).

const EASE_OUT = [0.23, 1, 0.32, 1];

function makeContainer(reduce) {
  return {
    hidden: {},
    show: {
      transition: {
        staggerChildren: reduce ? 0.02 : 0.06,
        delayChildren: 0.02,
      },
    },
  };
}

function makeItem(reduce) {
  return {
    hidden: { opacity: 0, y: reduce ? 0 : 10 },
    show: {
      opacity: 1,
      y: 0,
      transition: { duration: reduce ? 0.2 : 0.28, ease: EASE_OUT },
    },
  };
}

/**
 * Container that reveals its children with a staggered fade+slide when it
 * scrolls into view (once). Pass any layout classes via `className`.
 */
export function StaggerGroup({ children, className, as = "div", ...rest }) {
  const reduce = useReducedMotion();
  const MotionTag = motion[as] || motion.div;
  return (
    <MotionTag
      className={className}
      variants={makeContainer(reduce)}
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, margin: "-60px" }}
      {...rest}
    >
      {children}
    </MotionTag>
  );
}

/**
 * A single staggered child. Wrap each item you want revealed. On reduced
 * motion it still fades in but does not move.
 */
export function StaggerItem({ children, className, as = "div", ...rest }) {
  const reduce = useReducedMotion();
  const MotionTag = motion[as] || motion.div;
  return (
    <MotionTag className={className} variants={makeItem(reduce)} {...rest}>
      {children}
    </MotionTag>
  );
}
